import { formatRows, formatRowsVertical } from "./rows.js";
export const STRUCTURAL_HASH_COLUMNS = new Set(['rowId', 'rowAuthor']);
export function looksLikeSpeculativeHash(s) {
    return s.length >= 30 && /^[A-Za-z0-9+/]+=*$/.test(s) && s.endsWith('=');
}
export function isTruncatable(value, role) {
    return role === 'hash' || looksLikeSpeculativeHash(value);
}
export function uniquePrefixes(hashes, minLen = 8) {
    const out = new Map();
    for (const hash of hashes) {
        for (let len = minLen; len <= hash.length; len++) {
            const prefix = hash.slice(0, len);
            if (hashes.filter((candidate) => candidate.startsWith(prefix)).length === 1) {
                out.set(hash, prefix);
                break;
            }
        }
        if (!out.has(hash))
            out.set(hash, hash);
    }
    return out;
}
export function buildKeyLabelMap(session) {
    var _a, _b;
    const out = new Map();
    for (const key of (_b = (_a = session.keyVault) === null || _a === void 0 ? void 0 : _a.list()) !== null && _b !== void 0 ? _b : [])
        out.set(key.keyId, `$${key.label}`);
    for (const entry of session.aliases.list('key')) {
        if (!out.has(entry.hash))
            out.set(entry.hash, `$${entry.name}`);
    }
    return out;
}
export function collectTruncatableLiterals(value) {
    if (typeof value === 'string')
        return isTruncatable(value, 'cell') ? [value] : [];
    if (Array.isArray(value))
        return value.flatMap(collectTruncatableLiterals);
    if (value !== null && typeof value === 'object') {
        return Object.values(value).flatMap(collectTruncatableLiterals);
    }
    return [];
}
export function collectTruncatableFromColumnChanges(changes) {
    return changes.flatMap((change) => [
        ...(change.before === undefined ? [] : collectTruncatableLiterals(change.before)),
        ...(change.after === undefined ? [] : collectTruncatableLiterals(change.after)),
    ]);
}
export function collectTruncatableStrings(rows, structuralColumns = STRUCTURAL_HASH_COLUMNS) {
    const out = new Set();
    for (const row of rows) {
        for (const [column, value] of Object.entries(row)) {
            if (typeof value === 'string' && (structuralColumns.has(column) || looksLikeSpeculativeHash(value))) {
                out.add(value);
            }
        }
    }
    return [...out];
}
export function collectTruncatableFromResult(result) {
    switch (result.kind) {
        case 'select':
            return collectTruncatableStrings(result.rows.map((row) => (Object.assign(Object.assign({ rowId: row.rowId }, (row.author === undefined ? {} : { rowAuthor: row.author })), row.values))));
        case 'log':
            return result.rows.flatMap((row) => [row.fullHash, ...row.prev]);
        case 'add-member':
            return [result.memberId, result.database, result.entryHash];
        case 'insert':
        case 'update':
        case 'delete':
            return [result.rowId, result.entryHash];
        case 'bundle':
            return [result.entryHash];
        case 'alter-schema':
            return [result.schema, result.entryHash];
        case 'update-schema':
            return [result.group, result.entryHash];
        case 'update-ref':
            return [result.ref, result.group, result.entryHash];
        default:
            return [];
    }
}
function truncateHash(value, width, prefixes) {
    var _a;
    if (width === 'full')
        return value;
    return typeof width === 'number' ? value.slice(0, width) : (_a = prefixes.get(value)) !== null && _a !== void 0 ? _a : value;
}
export class HashDisplayContext {
    constructor(session, values) {
        this.session = session;
        this.keyMap = buildKeyLabelMap(session);
        const hashes = [...new Set([...values].filter((value) => value.length > 0))];
        this.prefixMap = session.hashWidth === 'auto' ? uniquePrefixes(hashes) : new Map();
    }
    formatString(value, opts = {}) {
        var _a;
        const role = (_a = opts.role) !== null && _a !== void 0 ? _a : 'cell';
        if (this.session.hashLabels) {
            const label = this.keyMap.get(value);
            if (label !== undefined)
                return label;
        }
        if (!isTruncatable(value, role))
            return value;
        let rendered = truncateHash(value, this.session.hashWidth, this.prefixMap);
        if (opts.hashPrefix === true)
            rendered = `#${rendered}`;
        return rendered;
    }
    formatValue(value, opts = {}) {
        if (value === undefined)
            return '';
        if (value === null)
            return 'null';
        if (typeof value === 'string')
            return this.formatString(value, opts);
        if (typeof value === 'object')
            return JSON.stringify(value);
        return String(value);
    }
}
export function createDisplayContext(session, values) {
    return new HashDisplayContext(session, values);
}
export function formatSessionRows(session, rows, columns, options) {
    var _a;
    const structuralColumns = (_a = options === null || options === void 0 ? void 0 : options.structuralColumns) !== null && _a !== void 0 ? _a : STRUCTURAL_HASH_COLUMNS;
    const ctx = createDisplayContext(session, collectTruncatableStrings(rows, structuralColumns));
    const formatOptions = { ctx, structuralColumns };
    return (options === null || options === void 0 ? void 0 : options.vertical) === true || session.outputMode === 'vertical'
        ? formatRowsVertical(rows, columns, formatOptions)
        : formatRows(rows, columns, formatOptions);
}
export function formatDisplayString(session, value, opts = {}) {
    var _a;
    const values = isTruncatable(value, (_a = opts.role) !== null && _a !== void 0 ? _a : 'cell') ? [value] : [];
    return createDisplayContext(session, values).formatString(value, opts);
}
